import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";

const BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5000/api";

interface Stats {
  totalRegistrations: number;
  totalTeams: number;
  totalIdeas: number;
}

export function Dashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    fetch(`${BASE}/admin/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error("Failed to fetch stats");
        return r.json();
      })
      .then((data) => setStats(data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="flex flex-col gap-[32px]">
      {/* Welcome Header */}
      <div className="h-[160px] rounded-[16px] px-[32px] py-[32px] bg-gradient-to-r from-[#F3F7FF] to-[#ECF3FF]">
        <p className="font-['Inter:Medium',sans-serif] font-medium leading-[16.5px] text-[#6e7891] text-[11px] tracking-[0.275px] uppercase">
          {today.toUpperCase()}
        </p>
        <h1 className="font-['Inter:Regular',sans-serif] font-normal leading-[42px] text-[28px] text-[#1a2332] mt-[8px]">
          Welcome, <span className="font-['Inter:Medium',sans-serif] font-medium text-blue-600">Admin</span>
        </h1>
        <p className="font-['Inter:Regular',sans-serif] font-normal leading-[21px] text-[#6e7891] text-[14px]">
          Manage hackathons, panels, teams and track progress.
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-300 text-red-700 rounded-[8px] text-[14px]">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-[24px]">
        <StatsCard
          icon="registrations"
          label="Total Registrations"
          value={loading ? null : stats?.totalRegistrations ?? 0}
          color="#0058AB"
        />
        <StatsCard
          icon="teams"
          label="Active Teams"
          value={loading ? null : stats?.totalTeams ?? 0}
          color="#FF9F43"
        />
        <StatsCard
          icon="ideas"
          label="Ideas Submitted"
          value={loading ? null : stats?.totalIdeas ?? 0}
          color="#00D68F"
        />
      </div>
    </div>
  );
}

function StatsCard({
  icon,
  label,
  value,
  color,
}: {
  icon: string;
  label: string;
  value: number | null;
  color: string;
}) {
  return (
    <div
      className="h-[177px] rounded-[16px] border-[0.667px] border-[rgba(228,236,244,0.4)] shadow-[0px_8px_32px_0px_rgba(31,38,135,0.08)] p-[24px]"
      style={{
        backgroundImage:
          "linear-gradient(136.592deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.6) 100%)",
      }}
    >
      <div
        className="size-[48px] rounded-[14px] shadow-[0px_4px_12px_0px_rgba(0,88,171,0.14)] flex items-center justify-center mb-[20px]"
        style={{ backgroundColor: `${color}0F` }}
      >
        <StatsIcon type={icon} color={color} />
      </div>
      <p className="font-['Inter:Medium',sans-serif] font-medium leading-[16.5px] text-[#8b93a8] text-[11px] tracking-[0.275px] uppercase mb-[8px]">
        {label}
      </p>
      <p
        className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[32px] text-[32px]"
        style={{ color }}
      >
        {value === null ? (
          <span className="inline-block w-12 h-8 bg-gray-200 animate-pulse rounded" />
        ) : (
          value
        )}
      </p>
    </div>
  );
}

function StatsIcon({ type, color }: { type: string; color: string }) {
  return (
    <svg className="size-[22px]" fill="none" viewBox="0 0 22 22">
      {type === "registrations" && (
        <>
          <path
            d="M14.6667 19.25V17.4167C14.6667 16.4442 14.2804 15.5116 13.5927 14.8239C12.9051 14.1363 11.9725 13.75 11 13.75H5.5C4.52754 13.75 3.59491 14.1363 2.90728 14.8239C2.21964 15.5116 1.83333 16.4442 1.83333 17.4167V19.25"
            stroke={color} strokeWidth="1.375" strokeLinecap="round" strokeLinejoin="round"
          />
          <path
            d="M8.25 10.0833C10.275 10.0833 11.9167 8.44171 11.9167 6.41667C11.9167 4.39162 10.275 2.75 8.25 2.75C6.22496 2.75 4.58333 4.39162 4.58333 6.41667C4.58333 8.44171 6.22496 10.0833 8.25 10.0833Z"
            stroke={color} strokeWidth="1.375" strokeLinecap="round" strokeLinejoin="round"
          />
        </>
      )}
      {type === "teams" && (
        <path
          d="M19.25 13.75C19.25 14.2362 19.0568 14.7025 18.713 15.0464C18.3692 15.3902 17.9029 15.5833 17.4167 15.5833H6.41667L2.75 19.25V4.58333C2.75 4.0971 2.94315 3.63079 3.28697 3.28697C3.63079 2.94315 4.0971 2.75 4.58333 2.75H17.4167C17.9029 2.75 18.3692 2.94315 18.713 3.28697C19.0568 3.63079 19.25 4.0971 19.25 4.58333V13.75Z"
          stroke={color} strokeWidth="1.375" strokeLinecap="round" strokeLinejoin="round"
        />
      )}
      {type === "ideas" && (
        <>
          <path
            d="M11 20.1667C16.0626 20.1667 20.1667 16.0626 20.1667 11C20.1667 5.93739 16.0626 1.83333 11 1.83333C5.93739 1.83333 1.83333 5.93739 1.83333 11C1.83333 16.0626 5.93739 20.1667 11 20.1667Z"
            stroke={color} strokeWidth="1.375" strokeLinecap="round" strokeLinejoin="round"
          />
          <path
            d="M11 5.5V11L14.6667 12.8333"
            stroke={color} strokeWidth="1.375" strokeLinecap="round" strokeLinejoin="round"
          />
        </>
      )}
    </svg>
  );
}
